// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TitleTypography, { TitleTypographyStyles } from "@awc/App/Components/Common/TitleTypography";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import { xParserOptions } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";

const enum fields {
    title = "title",
    description = "description",
    images = "images",
    desktopNumberOfImagesPerRow = "desktopNumberOfImagesPerRow",
    desktopMaximumImageWidthSpacePerRow = "desktopMaximumImageWidthSpacePerRow",
    maximumImageHeight = "maximumImageHeight",
}

interface ImageModel {
    fields: {
        imageSource: string;
        destination: LinkFieldValue;
        imageAltText: string;
        openInNewWindow: boolean;
    };
}

interface AddedServicesProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.images]: ImageModel[];
        [fields.desktopNumberOfImagesPerRow]: number;
        [fields.desktopMaximumImageWidthSpacePerRow]: number;
        [fields.maximumImageHeight]: number;
    };
}

interface ValueAddedServicesStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    description?: TypographyProps;
    image?: LazyImageProps;
    imageClickable?: ClickablePresentationProps;
    serviceImagesContainer?: InjectableCss;
    titleTypographyStyles?: TitleTypographyStyles;
}

export const valueAddedServicesStyles: ValueAddedServicesStyles = {};

const styles = valueAddedServicesStyles;
const ValueAddedServices: React.FC<AddedServicesProps> = ({ fields }) => {
    const linkUrls = useGetLinks(fields.images, o => o.fields.destination);

    const maxContainerWidth =
        fields.desktopNumberOfImagesPerRow &&
        fields.desktopNumberOfImagesPerRow * fields.desktopMaximumImageWidthSpacePerRow +
            (fields.desktopNumberOfImagesPerRow - 1) * 20;

    return (
        <StyledWrapper as="section" {...styles.container} data-test-selector="value-added-service">
            <TitleTypography extendedStyles={styles.titleTypographyStyles}>{fields.title}</TitleTypography>
            <Typography data-test-selector="value_added_services_description" {...styles.description}>
                {parse(fields.description, xParserOptions)}
            </Typography>
            <StyledWrapper
                {...styles.serviceImagesContainer}
                {...{
                    maxContainerWidth,
                }}
            >
                {linkUrls.map((link, index) => {
                    const imageLink = fields.images[index].fields;
                    const shouldOpenInNewWindow = imageLink.openInNewWindow;
                    return (
                        <Clickable
                            {...styles.imageClickable}
                            key={`${link.url}_${index}`}
                            target={shouldOpenInNewWindow ? "_blank" : undefined}
                            href={link.url}
                        >
                            <LazyImage
                                src={imageLink.imageSource}
                                altText={imageLink.imageAltText}
                                {...styles.image}
                                {...{ imageMaxHeight: fields.maximumImageHeight }}
                            />
                        </Clickable>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ValueAddedServices,
    definition: {
        group: XNGAGE_ELEMENTS as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "General",
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                isRequired: false,
                fieldType: "General",
            },
            {
                name: fields.images,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.imageAltText;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "imageSource",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Url",
                        },
                    },
                    {
                        name: "imageAltText",
                        displayName: "Image Alt Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.desktopNumberOfImagesPerRow,
                displayName: "Number Of Images Per Row For Desktop",
                fieldType: "Contextual",
                editorTemplate: "DropDownField",
                options: [
                    { value: 1 },
                    { value: 2 },
                    { value: 3 },
                    { value: 4 },
                    { value: 5 },
                    { value: 6 },
                    { value: 7 },
                    { value: 8 },
                ],
                sortOrder: 3,
                defaultValue: 8,
                hideEmptyOption: false,
                tooltip: "Not Choosing A value means fitting as many images as possible while wrapping the others",
            },
            {
                name: fields.desktopMaximumImageWidthSpacePerRow,
                displayName: "Maximum desktop image width",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "Contextual",
                isVisible: (item: HasFields) => item.fields.desktopNumberOfImagesPerRow,
                tooltip: "Space allocated to each image to calculate max row width based on number of images per row ",
            },
            {
                name: fields.maximumImageHeight,
                displayName: "Maximum image height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "Contextual",
                tooltip:
                    "By default, images will scale to meet the width of the screen for the number of images to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
            },
        ],
    },
};

export default widgetModule;
